import React, { memo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { TaskAltRounded } from '@mui/icons-material';
import type { Lesson } from '../../../services/timetableService';
import LessonCard from '../LessonCard';
import { sectionLabel } from '../timetableUtils';

export interface TrayItem {
  lesson: Lesson;
  singles: number;
  doubles: number;
}

interface UnplacedTrayProps {
  items: TrayItem[];
  /** Show the class on each card (when the tray spans several classes). */
  showClass: boolean;
  onDragStart: (lesson: Lesson, span: 1 | 2) => void;
  onDragEnd: () => void;
}

/** Periods still to place, as a strip above the week. Drag one onto a green cell. */
const UnplacedTray = memo<UnplacedTrayProps>(({ items, showClass, onDragStart, onDragEnd }) => {
  if (!items.length) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'text.secondary' }}>
        <TaskAltRounded fontSize="small" sx={{ color: 'success.main' }} />
        <Typography variant="body2" fontWeight={600} color="text.primary">Everything is placed</Typography>
        <Typography variant="caption">Drag a period here to take it off the week.</Typography>
      </Stack>
    );
  }
  const card = (item: TrayItem, span: 1 | 2, count: number) => (
    <Box key={`${item.lesson.key}-${span}`} sx={{ width: span === 2 ? 220 : 160, height: showClass ? 58 : 46, flexShrink: 0 }}>
      <LessonCard
        subjectId={item.lesson.subjectId}
        title={`${item.lesson.subjectName}${span === 2 ? ' · double' : ''}`}
        subtitle={showClass ? sectionLabel(item.lesson) : item.lesson.teacherName ?? 'No teacher'}
        meta={showClass ? item.lesson.teacherName : null}
        draggable={!!item.lesson.teacherId}
        onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', item.lesson.key); onDragStart(item.lesson, span); }}
        onDragEnd={onDragEnd}
        badge={<Typography component="span" sx={{ fontSize: '0.68rem', fontWeight: 700, mr: 0.25 }}>×{count}</Typography>}
      />
    </Box>
  );
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, maxHeight: 136, overflowY: 'auto' }}>
      {items.flatMap((item) => [
        ...(item.doubles ? [card(item, 2, item.doubles)] : []),
        ...(item.singles ? [card(item, 1, item.singles)] : []),
      ])}
    </Box>
  );
});

UnplacedTray.displayName = 'UnplacedTray';

export default UnplacedTray;
